/**
 * TreatmentFormIcon.tsx — the at-a-glance "how is this taken" mark on a
 * treatment card.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY (2026-08-17, business): the treatment cards were four blocks of text that
 * differed mainly by name and price. How a treatment is TAKEN is one of the
 * biggest factors in whether a patient wants it at all — and it was only
 * discoverable by reading the small line under the title. A mark makes it
 * scannable down the column.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT CLASSIFIES THE SERVER'S `form` STRING. It does NOT map a brand to a shape.
 *
 * R28 bans POM brand and delivery-form words from this tree, and `r28.spec.tsx`
 * scans SOURCE — comments included. So there is no lookup table here, no
 * `if (name === …)`, and no example names in the comments. `PlanOption.form`
 * arrives from prices-service and the only thing this file does is decide which
 * of two shapes to draw from it.
 *
 * UNKNOWN IS A FIRST-CLASS ANSWER. A `form` this does not recognise renders NO
 * icon rather than a default one: a wrong shape ("this is a pill" when it is
 * not) is a clinical mis-statement on a prescribing journey, and silence is the
 * safe failure. A new formulation from the catalogue therefore degrades to no
 * mark instead of a lie, with no portal release needed.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Accessibility: the icon is DECORATIVE and `aria-hidden`. The card already
 * states the form in words on the line beneath the title, so announcing it here
 * too would just repeat it — and if the icon were the only carrier, the meaning
 * would rest on a shape alone, which fails WCAG 1.4.1 for the same reason
 * colour alone does.
 */

/** What we can draw. Anything else ⇒ no mark (see the header). */
type Shape = 'injected' | 'swallowed';

/**
 * Classify the server's formulation string.
 *
 * Substring matching on a lower-cased value, deliberately: the catalogue is
 * free text maintained outside this repo, so an exact-match set would silently
 * stop working the first time someone writes it slightly differently. What we
 * match on are generic route-of-administration words, not product descriptors.
 */
export function shapeForForm(form: string | null | undefined): Shape | null {
  if (!form) return null;
  const f = form.toLowerCase();
  if (f.includes('inject') || f.includes('pen') || f.includes('subcutaneous')) return 'injected';
  if (f.includes('oral') || f.includes('tablet') || f.includes('capsule') || f.includes('pill')) {
    return 'swallowed';
  }
  return null;
}

export function TreatmentFormIcon({ form, size = 20 }: { form: string | null | undefined; size?: number }) {
  const shape = shapeForForm(form);
  if (!shape) return null;

  return (
    <span
      aria-hidden="true"
      style={{
        width: `${size + 18}px`,
        height: `${size + 18}px`,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: 'var(--gps-r-sm)',
        background: 'var(--gps-surface-2)',
        border: '1px solid var(--gps-border)',
        color: 'var(--gps-green-text)',
      }}
    >
      {shape === 'injected' ? (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          focusable="false"
        >
          <path d="M18 2l4 4" />
          <path d="M20.5 4.5L14 11" />
          <path d="M12.5 9.5L16 13" />
          <path d="M12.5 9.5L5 17l-2 4 4-2 7.5-7.5" />
        </svg>
      ) : (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          focusable="false"
        >
          <rect x="2.5" y="8" width="19" height="8" rx="4" />
          <path d="M12 8v8" />
        </svg>
      )}
    </span>
  );
}
